// ---------------------------------------------------------------------------
// shapes.js — Shapes / elements expansion (spec group F).
//
// Registers shape inserters (Ellipse, Triangle, Star, Polygon, Arrow), an SVG
// icon/logo importer, a contextual "Forma" property panel (stroke width, dashed
// stroke, rounded-rect radius, live polygon/star generator) and a free-draw
// "Pensula" panel (brush size + colour) into the DesignEditor foundation seam.
//
// Seam calls used (see the editor-API header in DesignEditor.js):
//   editor.addToolButton(...)             — shape + SVG inserters
//   editor.addPanelSection(...)           — "Forma" + "Pensula" sections
//   editor.registerSerializationProps(...) — persists the polygon-generator meta
//
// Ownership: this module owns NO fabric canvas mode and NO object:moving /
// path:created binding. Free-draw itself (isDrawingMode, the PencilBrush and its
// path:created snapshot) is owned by the foundation's setTool('draw'); we only
// expose UI that tweaks the already-live brush.
//
// SVG caveats: fabric.loadSVGFromString + groupSVGElements import shapes, paths
// and (flat) linear/radial gradients, but drop <mask>/<clipPath>, CSS-class
// styling and filter effects. Good enough for flat icons/logos; complex artwork
// may look different. See report.
// ---------------------------------------------------------------------------
import React, { Component } from 'react';
import { fabric } from 'fabric';

const SHAPE_TYPES = ['rect', 'circle', 'ellipse', 'triangle', 'polygon', 'polyline', 'line', 'path', 'group'];
const isShapeObject = (o) => !!o && SHAPE_TYPES.indexOf(o.type) !== -1;

const SHAPE_FILL = '#4a6fa5';
const SHAPE_STROKE = '#1e1e1e';

// --- geometry -------------------------------------------------------------
function buildStarPoints(spikes, outer, innerRatio) {
  const inner = outer * innerRatio;
  const pts = [];
  const step = Math.PI / spikes;
  let rot = -Math.PI / 2;
  for (let i = 0; i < spikes; i++) {
    pts.push({ x: Math.cos(rot) * outer, y: Math.sin(rot) * outer }); rot += step;
    pts.push({ x: Math.cos(rot) * inner, y: Math.sin(rot) * inner }); rot += step;
  }
  return pts;
}

function buildPolygonPoints(sides, radius) {
  const pts = [];
  const step = (2 * Math.PI) / sides;
  let rot = -Math.PI / 2;
  for (let i = 0; i < sides; i++) {
    pts.push({ x: Math.cos(rot) * radius, y: Math.sin(rot) * radius });
    rot += step;
  }
  return pts;
}

// Replace a polygon's points IN PLACE while keeping its on-canvas centre, so the
// generator sliders don't churn the selection or drift the shape. Recomputes
// width/height/pathOffset the same way fabric.Polyline._calcDimensions does,
// avoiding a dependency on that private method.
function updatePolygonPoints(o, raw) {
  const center = o.getCenterPoint();
  o.points = raw;
  const xs = raw.map((p) => p.x);
  const ys = raw.map((p) => p.y);
  const minX = Math.min.apply(null, xs);
  const maxX = Math.max.apply(null, xs);
  const minY = Math.min.apply(null, ys);
  const maxY = Math.max.apply(null, ys);
  o.width = maxX - minX;
  o.height = maxY - minY;
  o.pathOffset = { x: minX + o.width / 2, y: minY + o.height / 2 };
  o.setPositionByOrigin(center, 'center', 'center');
  o.dirty = true;
  o.setCoords();
}

// --- inserters ------------------------------------------------------------
function addCentered(editor, obj, label) {
  const { width, height } = editor.getArtboard();
  obj.set({ left: width / 2, top: height / 2, originX: 'center', originY: 'center' });
  editor.canvas.add(obj);
  editor.canvas.setActiveObject(obj);
  editor.requestRender();
  editor.pushHistory(label);
  editor.refreshLayers();
}

function addEllipse(editor) {
  addCentered(editor, new fabric.Ellipse({ rx: 60, ry: 42, fill: SHAPE_FILL, stroke: SHAPE_STROKE, strokeWidth: 0, name: 'Elipsa' }), 'Elipsa');
}

function addTriangle(editor) {
  addCentered(editor, new fabric.Triangle({ width: 100, height: 90, fill: SHAPE_FILL, stroke: SHAPE_STROKE, strokeWidth: 0, name: 'Triunghi' }), 'Triunghi');
}

function addStar(editor) {
  const poly = new fabric.Polygon(buildStarPoints(5, 55, 0.5), {
    fill: SHAPE_FILL, stroke: SHAPE_STROKE, strokeWidth: 0, name: 'Stea',
    _dePolyKind: 'star', _dePoints: 5, _deInnerRatio: 0.5,
  });
  addCentered(editor, poly, 'Stea');
}

function addPolygon(editor) {
  const poly = new fabric.Polygon(buildPolygonPoints(6, 55), {
    fill: SHAPE_FILL, stroke: SHAPE_STROKE, strokeWidth: 0, name: 'Poligon',
    _dePolyKind: 'polygon', _dePoints: 6,
  });
  addCentered(editor, poly, 'Poligon');
}

function addArrow(editor) {
  const line = new fabric.Line([-60, 0, 40, 0], { stroke: SHAPE_STROKE, strokeWidth: 4, strokeLineCap: 'round' });
  const head = new fabric.Triangle({ width: 22, height: 26, fill: SHAPE_STROKE, left: 50, top: 0, originX: 'center', originY: 'center', angle: 90 });
  addCentered(editor, new fabric.Group([line, head], { name: 'Sageata' }), 'Sageata');
}

// --- SVG import -----------------------------------------------------------
function placeSvg(editor, objects, options, name) {
  if (!objects || !objects.length) { editor.toast('SVG invalid sau gol.', 'error'); return; }
  const obj = fabric.util.groupSVGElements(objects, options);
  const { width, height } = editor.getArtboard();
  const scale = Math.min((width * 0.7) / (obj.width || 1), (height * 0.7) / (obj.height || 1), 1);
  obj.set({ originX: 'center', originY: 'center', left: width / 2, top: height / 2, scaleX: scale, scaleY: scale, name: name || 'SVG' });
  editor.canvas.add(obj);
  editor.canvas.setActiveObject(obj);
  editor.requestRender();
  editor.pushHistory('Import SVG');
  editor.refreshLayers();
}

// Import from raw SVG markup (loadSVGFromString) or, if `source` is a URL,
// from the network (loadSVGFromURL — subject to CORS on the icon host).
function importSvg(editor, source, name) {
  try {
    if (/^\s*<svg[\s>]|^\s*<\?xml/i.test(source)) {
      fabric.loadSVGFromString(source, (objs, opts) => placeSvg(editor, objs, opts, name));
    } else {
      fabric.loadSVGFromURL(source, (objs, opts) => placeSvg(editor, objs, opts, name));
    }
  } catch (e) {
    editor.toast('Importul SVG a esuat.', 'error');
  }
}

function pickAndImportSvg(editor) {
  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/svg+xml,.svg';
  input.onchange = () => {
    const f = input.files && input.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => importSvg(editor, String(reader.result || ''), f.name.replace(/\.svg$/i, ''));
    reader.onerror = () => editor.toast('Fisierul SVG nu a putut fi citit.', 'error');
    reader.readAsText(f);
  };
  input.click();
}

// --- helpers for the property panel --------------------------------------
function applyToShape(o, patch) {
  if (o.type === 'group' && o.forEachObject) {
    o.forEachObject((c) => c.set(patch));
    o.dirty = true;
  } else {
    o.set(patch);
  }
}

function readStrokeWidth(o) {
  if (o.type === 'group' && o._objects && o._objects.length) {
    const c = o._objects.find((x) => x.strokeWidth) || o._objects[0];
    return c.strokeWidth || 0;
  }
  return o.strokeWidth || 0;
}

function toHexColor(c) {
  if (typeof c !== 'string' || !c) return '#000000';
  if (c[0] === '#') return c.length >= 7 ? c.slice(0, 7) : c;
  const m = c.match(/rgba?\(([^)]+)\)/i);
  if (m) {
    const parts = m[1].split(',').map((s) => parseInt(s, 10));
    return '#' + parts.slice(0, 3).map((v) => ('0' + ((v || 0) & 255).toString(16)).slice(-2)).join('');
  }
  return '#000000';
}

// --- "Forma" property panel ----------------------------------------------
class ShapePanel extends Component {
  get obj() {
    const o = this.props.editor.getActive();
    return isShapeObject(o) ? o : null;
  }

  _after = (history, label) => {
    const o = this.obj;
    if (o) { o.dirty = true; o.setCoords(); }
    this.props.editor.requestRender();
    if (history === 'now') this.props.editor.pushHistory(label);
    else this.props.editor.pushHistoryDebounced(label);
    this.forceUpdate();
  };

  setStrokeWidth = (e) => {
    const w = parseInt(e.target.value, 10);
    const o = this.obj; if (!o) return;
    applyToShape(o, { strokeWidth: w });
    this._after('debounce', 'Grosime contur');
  };

  toggleDash = () => {
    const o = this.obj; if (!o) return;
    const on = !!(o.strokeDashArray && o.strokeDashArray.length);
    const d = Math.max(4, (readStrokeWidth(o) || 2) * 2);
    applyToShape(o, { strokeDashArray: on ? null : [d, d] });
    this._after('now', 'Linie intrerupta');
  };

  setDashSize = (e) => {
    const d = parseInt(e.target.value, 10);
    const o = this.obj; if (!o) return;
    applyToShape(o, { strokeDashArray: [d, d] });
    this._after('debounce', 'Model linie');
  };

  setRadius = (e) => {
    const r = parseInt(e.target.value, 10);
    const o = this.obj; if (!o || o.type !== 'rect') return;
    o.set({ rx: r, ry: r });
    this._after('debounce', 'Colturi rotunjite');
  };

  setPoints = (e) => {
    const n = parseInt(e.target.value, 10);
    const o = this.obj; if (!o || !o._dePolyKind) return;
    const raw = o._dePolyKind === 'star' ? buildStarPoints(n, 55, o._deInnerRatio || 0.5) : buildPolygonPoints(n, 55);
    updatePolygonPoints(o, raw);
    o.set('_dePoints', n);
    this._after('debounce', 'Poligon');
  };

  setInner = (e) => {
    const r = parseFloat(e.target.value);
    const o = this.obj; if (!o || o._dePolyKind !== 'star') return;
    updatePolygonPoints(o, buildStarPoints(o._dePoints || 5, 55, r));
    o.set('_deInnerRatio', r);
    this._after('debounce', 'Ascutime stea');
  };

  render() {
    const o = this.obj;
    if (!o) return null;
    const strokeWidth = readStrokeWidth(o);
    const dashOn = !!(o.strokeDashArray && o.strokeDashArray.length);
    const dashSize = dashOn ? (o.strokeDashArray[0] || 8) : 8;
    const isRect = o.type === 'rect';
    const isPoly = o.type === 'polygon' && !!o._dePolyKind;
    const isStar = isPoly && o._dePolyKind === 'star';
    const maxRadius = Math.max(2, Math.round(Math.min(o.width || 100, o.height || 100) / 2));

    return (
      <>
        <div className="de-row">
          <label className="de-label">Contur</label>
          <input type="range" min="0" max="40" step="1" value={strokeWidth} onChange={this.setStrokeWidth} className="de-range" />
          <span className="de-val">{strokeWidth}</span>
        </div>
        <div className="de-row">
          <button className={`de-btn-sm ${dashOn ? 'active' : ''}`} onClick={this.toggleDash} title="Linie intrerupta" style={{ width: 'auto', padding: '0 8px' }}>Punctat</button>
          {dashOn && (
            <>
              <input type="range" min="2" max="40" step="1" value={dashSize} onChange={this.setDashSize} className="de-range" />
              <span className="de-val">{dashSize}</span>
            </>
          )}
        </div>

        {isRect && (
          <div className="de-row">
            <label className="de-label">Raza</label>
            <input type="range" min="0" max={maxRadius} step="1" value={o.rx || 0} onChange={this.setRadius} className="de-range" />
            <span className="de-val">{Math.round(o.rx || 0)}</span>
          </div>
        )}

        {isPoly && (
          <div className="de-row">
            <label className="de-label">{isStar ? 'Colturi' : 'Laturi'}</label>
            <input type="range" min="3" max="12" step="1" value={o._dePoints || 6} onChange={this.setPoints} className="de-range" />
            <span className="de-val">{o._dePoints || 6}</span>
          </div>
        )}
        {isStar && (
          <div className="de-row">
            <label className="de-label">Ascutime</label>
            <input type="range" min="0.2" max="0.9" step="0.05" value={o._deInnerRatio || 0.5} onChange={this.setInner} className="de-range" />
            <span className="de-val">{Number(o._deInnerRatio || 0.5).toFixed(2)}</span>
          </div>
        )}
      </>
    );
  }
}

// --- "Pensula" free-draw panel -------------------------------------------
// Only visible while the foundation's free-draw tool is active (isDrawingMode &&
// no mode). Mutates the already-live brush; the foundation resets width/colour
// each time draw is (re)entered, so these settings apply to the current session.
class BrushPanel extends Component {
  get brush() { return this.props.editor.canvas.freeDrawingBrush; }

  setSize = (e) => {
    const v = parseInt(e.target.value, 10);
    if (this.brush) this.brush.width = v;
    this.forceUpdate();
  };

  setColor = (e) => {
    const v = e.target.value;
    if (this.brush) this.brush.color = v;
    this.forceUpdate();
  };

  render() {
    const b = this.brush;
    const size = b ? b.width : 3;
    const color = b ? b.color : '#000000';
    return (
      <>
        <div className="de-row">
          <label className="de-label">Marime</label>
          <input type="range" min="1" max="60" step="1" value={size} onChange={this.setSize} className="de-range" />
          <span className="de-val">{size}</span>
        </div>
        <div className="de-row">
          <label className="de-label">Culoare</label>
          <input type="color" className="de-color" value={toHexColor(color)} onChange={this.setColor} />
        </div>
      </>
    );
  }
}

// --- registration ---------------------------------------------------------
export function registerShapes(editor) {
  // Persist the polygon-generator meta so undo/redo + saved designs keep it.
  editor.registerSerializationProps(['_dePolyKind', '_dePoints', '_deInnerRatio']);

  editor.addToolButton({
    id: 'de-ellipse',
    title: 'Elipsa',
    icon: (<svg viewBox="0 0 24 24" width="18" height="18"><ellipse cx="12" cy="12" rx="9" ry="6" fill="none" stroke="currentColor" strokeWidth="2" /></svg>),
    onClick: (ed) => addEllipse(ed),
  });
  editor.addToolButton({
    id: 'de-triangle',
    title: 'Triunghi',
    icon: (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 4l9 16H3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" /></svg>),
    onClick: (ed) => addTriangle(ed),
  });
  editor.addToolButton({
    id: 'de-star',
    title: 'Stea',
    icon: (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 2l2.9 6.3 6.9.8-5.1 4.7 1.4 6.8L12 17.9 5.9 21.4l1.4-6.8L2.2 9.9l6.9-.8z" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" /></svg>),
    onClick: (ed) => addStar(ed),
  });
  editor.addToolButton({
    id: 'de-polygon',
    title: 'Poligon',
    icon: (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M12 3l7.8 5.7-3 9.2H7.2l-3-9.2z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" /></svg>),
    onClick: (ed) => addPolygon(ed),
  });
  editor.addToolButton({
    id: 'de-arrow',
    title: 'Sageata',
    icon: (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M3 12h13M13 6l7 6-7 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>),
    onClick: (ed) => addArrow(ed),
  });
  editor.addToolButton({
    id: 'de-svg',
    title: 'Importa SVG (iconita / logo)',
    icon: (<svg viewBox="0 0 24 24" width="18" height="18"><path d="M6 3h9l4 4v14a1 1 0 01-1 1H6a1 1 0 01-1-1V4a1 1 0 011-1z" fill="none" stroke="currentColor" strokeWidth="1.6" /><text x="12" y="16" textAnchor="middle" fontSize="7" fill="currentColor">SVG</text></svg>),
    onClick: (ed) => pickAndImportSvg(ed),
  });

  editor.addPanelSection({
    id: 'de-shape',
    title: 'Forma',
    order: 20,
    when: (o) => isShapeObject(o),
    render: (ed) => <ShapePanel editor={ed} />,
  });

  editor.addPanelSection({
    id: 'de-brush',
    title: 'Pensula (desen liber)',
    order: 30,
    when: () => editor.canvas.isDrawingMode && editor.getMode() == null,
    render: (ed) => <BrushPanel editor={ed} />,
  });
}

export default registerShapes;
